import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GeneralCommonHeader } from "../common/GeneralCommonHeader";
import {
  DISPLAY_HEADER_ICON_BACK,
  DISPLAY_PAGE_CHOOSE_MODEL,
  LANGUAGE_ENGLISH,
  LANGUAGE_JAPANESE,
  PAGE_NAME_ENGINE_LABEL_LOCATION,
} from "../../constants/common_constants";
import { calcCurrentDatetime, calcUtcDatetime } from "../../utils/basis_util";
import { EngineModelNextPageButton } from "../../features/components/select_model/ui_elements/EngineModelNextPageButton";
import { useDtsAppContext } from "../provider/DtsAppContextProvider";
import { InputSubjectName } from "../../features/components/select_model/ui_elements/InputSubjectName";
import { InputMachineNumber } from "../../features/components/select_model/ui_elements/InputMachineNumber";
import { IEngineModelType } from "../../features/components/select_model/types/engine_model_type";
import { EngineModelAccordionList } from "../../features/components/select_model/ui_elements/EngineModelAccordionList";
import { useApi, useErrorDialog, useInitUsageLog } from "../../hooks";
import { useTranslation } from "react-i18next";
import { getEngineModel } from "../../features/api/dts_api_wrapper";

// 型式選択画面
export const SelectModel = () => {
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();
  const { handleApi } = useApi();

  const { dacLanguage } = useDtsAppContext();
  const userId =
    localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`
    ) || "";
  const displayPage = DISPLAY_PAGE_CHOOSE_MODEL;
  const displayDateTime = calcUtcDatetime(); //現在日時(UTC時刻)
  const currentDatetime = calcCurrentDatetime(); //現在日時
  const language = dacLanguage || localStorage.getItem("language"); //言語

  // 案件名を格納, ページ読み込み時の日時を初期値として設定
  const [subjectName, setSubjectName] = useState<string>(currentDatetime);

  // 機番を格納, 未入力を初期値として設定
  const [machineNumber, setMachineNumber] = useState<string>("");

  // 選択されたモデル名を格納, 未入力を初期値として設定
  const [selectedModel, setSelectedModel] = useState({
    modelName: "",
    modelNumber: "",
    dtcSearchData: "",
    modelType: "",
  });

  // 取得した型式リストを格納, 空のオブジェクトを初期値として設定
  const [engineModelList, setEngineModelList] = useState<IEngineModelType>({
    model: {
      language: "",
      models: [],
    },
  });

  // 初期表示時の利用ログ登録
  const { handleInit: initUsageLog } = useInitUsageLog({
    userId,
    displayPage,
    displayDateTime,
  });

  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  //ページ表示時に行うEngineModelSelectionの処理
  const handleEngineModelSelectionInit = async () => {
    try {
      console.log("handleEngineModelSelectionInit start");
      //userIdが読み込めなかった際の処理は, intUsageLog()に記述
      //利用ログ登録
      const isError = await initUsageLog();
      if (isError) {
        return;
      }

      //型式取得
      const engineModelResult = await handleApi(
        getEngineModel,
        userId,
        language
      );
      //型式取得でエラーが出た場合, トップページに遷移
      if ("errorMessage" in engineModelResult) {
        console.log("errorMessage: ", engineModelResult.errorMessage);
        throw new Error("failed to get engine model");
      }
      //データが空かどうか判別, エラーダイアログ表示(トップページに遷移)
      const engineModelToJson: IEngineModelType = JSON.parse(
        JSON.stringify(engineModelResult)
      );
      if (!engineModelToJson.model.models.length) {
        throw new Error("engine model is empty");
      }

      //取得した型式をpriorityでソートしStateに保存
      sortEngineModelData(engineModelResult);
      const engineModelStr = JSON.stringify(engineModelResult);
      setEngineModelList(JSON.parse(engineModelStr));
    } catch (err) {
      console.log(`[${displayPage}]err: `, err);
      if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        //LocalStorageのセッション情報クリア
        const userpool_client_id =
          process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
        );

        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
          "navigateToTopPage"
        ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
      }
    }
  };

  useEffect(() => {
    // 言語がなければトップページへ遷移
    if (!language) {
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "navigateToTopPage"
      );
    } else {
      handleEngineModelSelectionInit();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, []);

  // アコーディオンメニューをレンダリングする関数
  const renderEngineModelAccordion = () => {
    return engineModelList.model.models.map((models, index) => (
      <EngineModelAccordionList
        models={models}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        isLastList={engineModelList.model.models.length === index + 1}
        key={models.model_group.label}
      />
    ));
  };

  return (
    // ページ全体の高さは100svh, スクロール不可に設定
    <div className="flex flex-col h-svh overflow-hidden">
      <GeneralCommonHeader
        title={t("型式選択画面.型式選択")} //型式選択
        displayheaderIcon={DISPLAY_HEADER_ICON_BACK}
        onBtnClick={() => {
          navigate(-1);
        }}
      />
      {/* header以下 */}
      <div className="flex flex-col justify-between m-4 h-full overflow-hidden whitespace-nowrap">
        {/* 案件名 */}
        <div className="mb-2 flex items-center justify-center">
          <p className="p-1">
            {t("型式選択画面.案件名")} :{/* 案件名： */}{" "}
          </p>
          <InputSubjectName
            subjectName={subjectName}
            setSubjectName={setSubjectName}
          />
        </div>
        {/* 型式選択 */}
        <div className="mb-2">
          <p className="px-1">
            {t("型式選択画面.エンジンモデルを選択してください")}：
            {/* 型式を選択してください */}
          </p>
          <p className="px-1 whitespace-normal">
            {t(
              "型式選択画面.利用可能または適用可能なモデルのみをリストアップしています。"
            )}
          </p>
          <div className="flex flex-wrap px-1">
            <div className="pr-2">{t("型式選択画面.アルファベット")},</div>
            <div className="pr-2">{t("型式選択画面.数字")}</div>
          </div>
          <div className="flex flex-wrap px-1">
            <div className="pr-2">{t("型式選択画面.英数字")}</div>
          </div>
        </div>
        <div className="grow mb-2 overflow-scroll overflow-x-hidden">
          {/* 型式リスト表示 */}
          {renderEngineModelAccordion()}
        </div>
        {/* 機番入力 */}
        <div className="mb-2">
          <p className="p-1">
            {t("型式選択画面.機番入力")} :{/* 機番入力： */}
          </p>
          <InputMachineNumber
            machineNumber={machineNumber}
            setMachineNumber={setMachineNumber}
          />
          {/* 型式確認方法 */}
          <div className="flex justify-end p-1">
            {/* 型式参照ファイル(PDF)を別タブで開く */}
            <p className="self-end justify-self-end  py-1">
              {language === LANGUAGE_JAPANESE &&
                t("型式選択画面.型式確認方法は")}
              <Link
                to={`/${PAGE_NAME_ENGINE_LABEL_LOCATION}`}
                target="_blank"
                className="text-sub-blue underline"
              >
                {t("型式選択画面.コチラ")} {/* コチラ */}
              </Link>
              {language === LANGUAGE_ENGLISH &&
                t("型式選択画面.型式確認方法は")}
              {/* 型式確認方法は */}
            </p>
          </div>
        </div>
        <div className="flex justify-center items-center">
          <EngineModelNextPageButton
            disabled={!(selectedModel.modelName && subjectName !== "")}
            userId={userId}
            subjectName={subjectName}
            model={selectedModel.modelNumber}
            machineNumber={machineNumber}
            dtcSearchData={selectedModel.dtcSearchData}
            modelType={selectedModel.modelType}
          />
        </div>
      </div>
    </div>
  );
};

/* 取得した型式リストをpriorityに従ってソートする関数 */
const sortEngineModelData = (unsortedData: IEngineModelType) => {
  unsortedData.model.models.sort((a, b) =>
    //前(a)の値が後(b)の値より小さい時, aとbの順番はそのまま.
    a.model_group.priority < b.model_group.priority ? -1 : 1
  );
  unsortedData.model.models.map((models) => {
    return models.model_group.model_items.sort((a, b) =>
      //前(a)の値が後(b)の値より小さい時, aとbの順番はそのまま.
      a.priority < b.priority ? -1 : 1
    );
  });
  return unsortedData;
};
